import React, { FC, useCallback, useEffect, useState } from 'react';
import { UserInfo } from '../../../common/model/dto/user-info';
import { SlotInfo, View, Views } from 'react-big-calendar';
import OvDailyDataCalendarHoc from '../molecules/OvDailyDataCalendarHoc';
import OvCalendarView from './OvCalendarView';
import styled from 'styled-components';
import breakpoints from '../../../design-system/breakpoints';
import OvDailyDataDetailsInCalendar from '../molecules/OvDailyDataDetailsInCalendar';
import DateUtils from '../../../common/utils/services/date-utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRouteUrl } from '../../../hooks/use-route-url';
import moment from 'moment';
import queryString from 'query-string';
import { DailyDataService } from '../../../services/daily-data.service';
import { DailyData } from '../../../common/model/dto/daily-data';
import { setMonth, setYear } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import { useQuery } from '@tanstack/react-query';
import { DailyDataRange, FetchDailyData } from './OvUserCharts';

const OvPatientInfoCalendar: FC<{
  /** Fetches the patient's daily data for the visible calendar month range. */
  fetchDailyData: FetchDailyData;
  /** Stable cache-key prefix for the calendar daily-data query. */
  dailyDataQueryKey: unknown[];
  selectedPatient?: UserInfo;
}> = ({ selectedPatient, fetchDailyData, dailyDataQueryKey }) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const url = useRouteUrl();

  const [defaultCalendarDate, setDefaultCalendarDate] = useState<Date>();
  const [visibleRange, setVisibleRange] = useState<Partial<DailyDataRange>>({});
  const [selectedDay, setSelectedDay] = useState<Timestamp | undefined>();
  const [selectedDailyDataInCalendar, setSelectedDailyDataInCalendar] =
    useState<DailyData | undefined>();

  const { data: dailyDataList = [] } = useQuery({
    queryKey: [
      ...dailyDataQueryKey,
      visibleRange.startDate?.toISOString(),
      visibleRange.endDate?.toISOString(),
    ],
    queryFn: () =>
      fetchDailyData({
        startDate: visibleRange.startDate!,
        endDate: visibleRange.endDate!,
      }),
    enabled:
      !!selectedPatient?.document_id &&
      !!visibleRange.startDate &&
      !!visibleRange.endDate,
  });

  const handleNavigationBetweenMonths = useCallback(
    (date: Date, view: View) => {
      if (view === Views.MONTH) {
        const firstVisibleDay = moment(date)
          .startOf('month')
          .subtract(20, 'days') // 7days needed because of the layout of the calendar, and another 10 day to count post ovulation days
          .toDate();
        const lastVisibleDay = moment(date)
          .endOf('month')
          .add(7, 'days')
          .toDate();

        setVisibleRange({
          startDate: firstVisibleDay,
          endDate: lastVisibleDay,
        });
      }
    },
    []
  );

  useEffect(() => {
    const visibleMonth: number = parseInt(
      queryString.parseUrl(search).query['visible-month'] as string
    );
    const visibleYear: number = parseInt(
      queryString.parseUrl(search).query['visible-year'] as string
    );

    let date = new Date();

    if (visibleMonth && visibleYear) {
      date = setMonth(date, visibleMonth - 1);
      date = setYear(date, visibleYear);
    }

    setDefaultCalendarDate(date);
    handleNavigationBetweenMonths(date, Views.MONTH);

    return function cleanup() {
      setSelectedDay(undefined);
      setSelectedDailyDataInCalendar(undefined);
    };
  }, [handleNavigationBetweenMonths, search]);

  const handleSlotSelect = (slotInfo: SlotInfo) => {
    const day = slotInfo.start as Date;

    setSelectedDay(Timestamp.fromDate(day));
    setSelectedDailyDataInCalendar(
      dailyDataList.find((dailyData) =>
        DateUtils.areSameDays(day, moment(dailyData.day, 'YYYY-MM-DD').toDate())
      )
    );
  };

  return (
    <Wrapper>
      <ContentWrapper>
        <OvCalendarView
          events={DailyDataService.mapDailyDataToCalendarEvents(dailyDataList)}
          selectedDay={selectedDay}
          style={{
            maxWidth: '100%',
            height: 600,
          }}
          onSelect={(slotInfo) => handleSlotSelect(slotInfo)}
          onNavigate={(date: Date, view: View) => {
            navigate(
              `${url}?visible-month=${DateUtils.formatDate(
                date,
                'MM'
              )}&visible-year=${DateUtils.formatDate(date, 'YYYY')}`,
              { replace: true }
            );
          }}
          headerHoc={OvDailyDataCalendarHoc}
          defaultDate={defaultCalendarDate}
        />

        <OvDailyDataDetailsInCalendar
          selectedDay={selectedDay}
          dailyData={selectedDailyDataInCalendar}
          selectedUser={selectedPatient}
        />
      </ContentWrapper>
    </Wrapper>
  );
};

export default OvPatientInfoCalendar;

const Wrapper = styled.div``;

const ContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;

  @media (max-width: ${breakpoints.sm}) {
    flex-wrap: wrap;
  }
`;
